import { Icon } from "@mui/material";
import { UserContext } from "App";
import MDBox from "components/MDBox";
import { useContext, useEffect, useState, useMemo } from "react";
import Swal from "sweetalert2";
import { useNavigate, useLocation } from "react-router-dom";
import AppointmentsApi from "../../../../Classes/API/Appointments/AppointmentsApi";
import AccSessionDiv from "./AccSessionDiv";
import { commonFlexStyles } from "../../../../utils/themeConstants";

const AppHourCell2 = ({
  AppHour,
  LoadReminders,
  AppDay,
  GetAppDayById,
  EnableFacilityModal,
  EnableAppointmentModal,
  setMovingSession,
  PasteSession,
  setIsLoaded,
  Reminders,
  isOperationsEnabled,
}) => {
  const user = useContext(UserContext);
  const location = useLocation();
  const [ShowReminders, setShowReminders] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const targetId = queryParams.get("targetId");
    if (targetId && document.getElementById(targetId)) {
      setTimeout(
        () =>
          document
            .getElementById(targetId)
            .scrollIntoView({ behavior: "smooth", block: "center" }),
        300,
      );
    }
  }, [location.search, AppHour]);

  // --- Reminder Logic (Re-eval) ---
  const activeReminders = useMemo(() => {
    return (
      Reminders?.filter(
        (r) =>
          r.appointmentHourId == AppHour.id &&
          r.patient.countSinceLastEval >= r.patient.lastEvalReminder,
      ) || []
    );
  }, [Reminders, AppHour.id]);

  const hasCriticalReminder = activeReminders.length > 0;

  const resetEval = async (patientId) => {
    let res = await AppointmentsApi.ResetCountSinceLastEval({
      SearchInt: patientId,
    });
    if (res.status === "Success") {
      Swal.fire("Success", "Reset successful", "success");
      LoadReminders();
    }
  };

  const handleAddMessage = async () => {
    const { value: message } = await Swal.fire({
      title: "Enter Message",
      input: "text",
      inputPlaceholder: "Type your message here...",
      showCancelButton: true,
      confirmButtonText: "Add",
      cancelButtonText: "Cancel",
    });

    if (message) {
      Swal.fire({
        title: "Creating Message...",
        text: "Please wait...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const requestBody = {
        AccountingSessionId: 0,
        AppointmentHourId: AppHour.id,
        IsDummy: true,
        PartitionId: null,
        BuildingId: null,
        PatientId: 0,
        PatientName: message,
        SessionName: "Message",
      };

      try {
        const res = await AppointmentsApi.LinkAccountingSessionToAppointmentHour(
          requestBody,
        );
        if (res.status === "Success") {
          Swal.fire("Success", "Message added successfully", "success").then(() => {
            setIsLoaded(false);
            GetAppDayById(AppDay.id);
          });
        } else {
          Swal.fire("Error", res.message, "error");
        }
      } catch (err) {
        Swal.fire("Error", "Something went wrong", "error");
      }
    }
  };

  const deleteAppt = async (acc, isDummy) => {
    if ((!["Admin", "Owner", "HeadAccounting"].includes(user.userType)) && user.userType !== "SubAdmin") {
      return Swal.fire("Denied", "Admin only", "error");
    }
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (confirm.isConfirmed) {
      Swal.fire({
        title: "Deleting...",
        text: "Please wait while we update the records.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        let res = await AppointmentsApi.ClearAccountingSessionAppointmentHour({
          SearchInt: acc.id,
          IsDummy: isDummy,
        });

        if (res.status === "Success") {
          await Swal.fire({
            title: "Deleted!",
            text: "The appointment has been removed.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });

          setIsLoaded(false);
          GetAppDayById(AppDay.id);
        } else {
          Swal.fire("Error", res.message || "Something went wrong", "error");
        }
      } catch (error) {
        Swal.fire("Error", "Failed to connect to the server", "error");
      }
    }
  };

  const sessions = [
    ...(AppHour.getListedAccountingSessions || []).map((s) => ({
      ...s,
      isDummy: false,
    })),
    ...(AppHour.getListedAccountingSessionsDummy || []).map((s) => ({
      ...s,
      isDummy: true,
    })),
  ];

  return (
    <MDBox
      sx={{
        width: "380px",
        margin: "2px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {sessions.length === 0 ? (
        <div
          style={{
            minHeight: "185px",
            border: "2px dashed #cbd5e1",
            borderRadius: "16px",
            backgroundColor: "#f8fafc",
            gap: "10px",
            ...commonFlexStyles,
          }}
        >
          <button
            className="btn btn-white shadow-sm"
            onClick={() => EnableAppointmentModal(true, AppHour)}
            title="Add Appointment"
          >
            <Icon color="primary">add</Icon>
          </button>
          <button
            className="btn btn-white shadow-sm"
            onClick={handleAddMessage}
            title="Add Message"
          >
            <Icon color="info">chat</Icon>
          </button>
          <button
            className="btn btn-white shadow-sm"
            onClick={() => PasteSession(AppHour)}
            title="Paste Appointment"
          >
            <Icon color="action">content_paste</Icon>
          </button>
        </div>
      ) : (
        sessions.map((s) => (
          <AccSessionDiv
            key={s.isDummy ? `d-${s.id}` : `r-${s.id}`}
            AccSession={s}
            isDummy={s.isDummy}
            AppHour={AppHour}
            hasReminder={hasCriticalReminder}
            setIsLoaded={setIsLoaded}
            setMovingSession={setMovingSession}
            EnableFacilityModal={EnableFacilityModal}
            isOperationsEnabled={isOperationsEnabled}
            DeleteAppointment={deleteAppt}
            GoToAccSessionDetails={(acc) =>
              navigate("/AccountingSessions/Details", {
                state: { SearchSession: acc },
              })
            }
          />
        ))
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "10px",
          marginTop: "-10px",
          zIndex: 2,
        }}
      >
        <button
          onClick={() => EnableAppointmentModal(true, AppHour)}
          title="Add Appointment"
          style={{
            ...commonFlexStyles,
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: "50%",
            width: "35px",
            height: "35px",
            boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
            cursor: "pointer",
          }}
        >
          <Icon sx={{ fontSize: 18 }}>add</Icon>
        </button>
        <button
          onClick={handleAddMessage}
          title="Add Message"
          style={{
            ...commonFlexStyles,
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: "50%",
            width: "35px",
            height: "35px",
            boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
            cursor: "pointer",
          }}
        >
          <Icon sx={{ fontSize: 18 }}>chat</Icon>
        </button>
        <button
          onClick={() => PasteSession(AppHour)}
          title="Paste Appointment"
          style={{
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: "50%",
            width: "35px",
            height: "35px",
            boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
            cursor: "pointer",
            ...commonFlexStyles,
          }}
        >
          <Icon sx={{ fontSize: 18 }}>content_paste</Icon>
        </button>
        <button
          onClick={() => setShowReminders(!ShowReminders)}
          style={{
            background: hasCriticalReminder ? "#f5365c" : "#344767",
            color: "#fff",
            border: "none",
            borderRadius: "50%",
            width: "35px",
            height: "35px",
            boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
            cursor: "pointer",
            ...commonFlexStyles,
          }}
        >
          <Icon sx={{ fontSize: 18 }}>
            {ShowReminders ? "close" : "notifications"}
          </Icon>
        </button>
      </div>

      {ShowReminders && (
        <div
          style={{
            marginTop: "15px",
            background: "#fff",
            padding: "12px",
            borderRadius: "12px",
            border: "1px solid #fee2e2",
            boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
          }}
        >
          <h6 style={{ fontSize: "0.7rem", color: "red", marginBottom: "8px" }}>
            RE-EVALUATION NEEDED
          </h6>
          {activeReminders.length === 0 ? (
            <small>No reminders</small>
          ) : (
            activeReminders.map((r) => (
              <div
                key={r.patient.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                  alignItems: "center",
                }}
              >
                <small style={{ fontWeight: "bold" }}>
                  {r.patient.getUser.name} ({r.patient.countSinceLastEval} sess)
                </small>
                <button
                  className="btn btn-xs btn-primary"
                  onClick={() => resetEval(r.patient.id)}
                >
                  <Icon sx={{ fontSize: 14 }}>undo</Icon>
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </MDBox>
  );
};

export default AppHourCell2;
